/**
 * PlannerPublic — Page publique de disponibilité (style Doodle)
 * URL permanente : /planificateur
 * Accessible sans authentification.
 */
import { useState, useEffect, useCallback } from 'react';
import { MobileHeader } from '../components/MobileHeader';
import { format, parseISO, isPast } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Users, ChevronLeft, Check, X, HelpCircle, Plus, Pencil, Trash2, ChevronDown, User, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Constants ────────────────────────────────────────────────────────────────
const RESP_CONFIG = {
  0: { icon: <HelpCircle size={14} className="text-slate-300" />, bg: 'bg-slate-50', label: '?' },
  1: { icon: <Check size={14} className="text-emerald-600" />,   bg: 'bg-emerald-50', label: '✓' },
  2: { icon: <X size={14} className="text-red-500" />,           bg: 'bg-red-50',     label: '✗' },
};

// 3-state cycle: 0 → 1 → 2 → 0
const cycleResp = (v) => ((v ?? 0) + 1) % 3;

// ── API helpers ───────────────────────────────────────────────────────────────
const api = {
  get:    (path)       => fetch(`${API}/api${path}`).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
  post:   (path, body) => fetch(`${API}/api${path}`, { method: 'POST',   headers: {'Content-Type':'application/json'}, body: JSON.stringify(body) }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
  put:    (path, body) => fetch(`${API}/api${path}`, { method: 'PUT',    headers: {'Content-Type':'application/json'}, body: JSON.stringify(body) }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
  delete: (path)       => fetch(`${API}/api${path}`, { method: 'DELETE' }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
};

// ── Session label ─────────────────────────────────────────────────────────────
function SessionLabel({ session, compact = false }) {
  const d = session.datetime_start ? parseISO(session.datetime_start) : null;
  if (!d) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="text-center leading-tight">
      <div className="text-[10px] font-bold text-slate-500 uppercase">{format(d, 'EEE', { locale: fr })}</div>
      <div className="text-sm font-bold text-slate-800">{format(d, 'd MMM', { locale: fr })}</div>
      {!compact && <div className="text-[10px] text-slate-400">{format(d, 'HH:mm')}</div>}
    </div>
  );
}

// ── Role badge ────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  if (!role) return <span className="text-[10px] text-slate-300">—</span>;
  return (
    <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full',
      role === 'leader' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600')}>
      {role === 'leader' ? 'L' : 'F'}
    </span>
  );
}

// ── Add / Edit entry form ─────────────────────────────────────────────────────
function EntryForm({ courseId, sessions, enrolledStudents, existingEntry, onSaved, onCancel }) {
  const isEdit = !!existingEntry;
  const [name, setName] = useState(existingEntry?.name || '');
  const [nameConfirm, setNameConfirm] = useState('');
  const [role, setRole] = useState(existingEntry?.role || '');
  const [responses, setResponses] = useState(existingEntry?.session_responses || {});
  const [saving, setSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleResp = (sid) => setResponses(r => ({ ...r, [sid]: cycleResp(r[sid] ?? 0) }));

  const handleSelectStudent = (s) => {
    setName(s.name);
    if (!role) setRole(s.role || '');
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error('Veuillez entrer votre nom');
    setSaving(true);
    try {
      if (isEdit) {
        if (!nameConfirm.trim()) return toast.error('Confirmez votre nom pour modifier');
        await api.put(`/public/planner/entries/${existingEntry.id}`, {
          name_confirm: nameConfirm,
          name,
          role,
          session_responses: responses,
        });
        toast.success('Disponibilités mises à jour !');
      } else {
        await api.post(`/public/planner/${courseId}/entries`, {
          name,
          role,
          session_responses: responses,
        });
        toast.success('Disponibilités enregistrées !');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#0288D1]/30 p-5 shadow-sm">
      <h3 className="font-bold text-slate-800 mb-4 text-sm">
        {isEdit ? 'Modifier mes disponibilités' : 'Ajouter mes disponibilités'}
      </h3>

      {/* Name */}
      <div className="mb-3">
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Votre nom</label>
        <div className="relative">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder="Prénom Nom"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#0288D1] pr-9"
            data-testid="planner-name-input"
          />
          {enrolledStudents.length > 0 && (
            <button type="button" onClick={() => setShowDropdown(d => !d)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <ChevronDown size={14} />
            </button>
          )}
          {showDropdown && enrolledStudents.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {enrolledStudents
                .filter(s => !name || s.name.toLowerCase().includes(name.toLowerCase()))
                .map(s => (
                  <button key={s.id} type="button"
                    onClick={() => handleSelectStudent(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                    <User size={12} className="text-slate-400 shrink-0" />
                    <span>{s.name}</span>
                    {s.role && <RoleBadge role={s.role} />}
                  </button>
                ))}
              {enrolledStudents.filter(s => !name || s.name.toLowerCase().includes(name.toLowerCase())).length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">Aucun résultat — tapez librement</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Role */}
      <div className="mb-4">
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Rôle</label>
        <div className="flex gap-2">
          {[{ v: 'follower', label: 'Follower', cls: 'bg-pink-500 text-white border-pink-500' },
            { v: 'leader',   label: 'Leader',   cls: 'bg-[#0288D1] text-white border-[#0288D1]' }].map(r => (
            <button key={r.v} type="button"
              onClick={() => setRole(role === r.v ? '' : r.v)}
              className={cn('flex-1 h-9 rounded-xl border font-semibold text-sm transition-all',
                role === r.v ? r.cls : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}
              data-testid={`planner-role-${r.v}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Session toggles */}
      <div className="mb-4">
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-2">
          Présence par séance (cliquez pour changer)
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {sessions.map(s => {
            const r = responses[s.id] ?? 0;
            const cfg = RESP_CONFIG[r];
            return (
              <button key={s.id} type="button" onClick={() => toggleResp(s.id)}
                className={cn('flex items-center gap-2 rounded-xl border px-3 py-2 transition-all', cfg.bg,
                  r === 1 ? 'border-emerald-200' : r === 2 ? 'border-red-200' : 'border-slate-200 hover:border-slate-300')}
                data-testid={`planner-session-${s.id}`}>
                {cfg.icon}
                <div className="text-left leading-tight min-w-0">
                  <div className="text-[10px] font-bold text-slate-500 truncate">
                    {s.datetime_start ? format(parseISO(s.datetime_start), 'EEE d MMM', { locale: fr }) : '—'}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {s.datetime_start ? format(parseISO(s.datetime_start), 'HH:mm') : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex gap-4 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><HelpCircle size={11} /> Ne sait pas</span>
          <span className="flex items-center gap-1"><Check size={11} className="text-emerald-500" /> Présent</span>
          <span className="flex items-center gap-1"><X size={11} className="text-red-400" /> Absent</span>
        </div>
      </div>

      {/* Name confirmation (edit only) */}
      {isEdit && (
        <div className="mb-4">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
            Confirmez votre nom pour valider
          </label>
          <input
            value={nameConfirm}
            onChange={e => setNameConfirm(e.target.value)}
            placeholder={`Tapez "${existingEntry.name}"`}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#0288D1]"
            data-testid="planner-name-confirm"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-xl hover:bg-slate-50 transition-all">
          Annuler
        </button>
        <button type="button" onClick={handleSubmit} disabled={saving}
          className="px-5 py-2 text-sm font-bold bg-[#0288D1] text-white rounded-xl hover:bg-[#0277BE] transition-all disabled:opacity-50"
          data-testid="planner-submit-btn">
          {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────
function DeleteConfirm({ entry, onConfirm, onCancel }) {
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm(val);
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
        <h3 className="font-bold text-slate-800 mb-2">Supprimer ma ligne</h3>
        <p className="text-sm text-slate-500 mb-4">Confirmez votre nom pour supprimer <strong>{entry.name}</strong> :</p>
        <input value={val} onChange={e => setVal(e.target.value)} placeholder={entry.name}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:border-red-400"
          data-testid="planner-delete-confirm" />
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-50 rounded-xl">Annuler</button>
          <button onClick={handleConfirm} disabled={loading}
            className="px-4 py-2 text-sm font-bold bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50"
            data-testid="planner-delete-btn">
            {loading ? '…' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Doodle Grid ───────────────────────────────────────────────────────────────
function DoodleGrid({ data, onRefresh, isAdmin }) {
  const { course, sessions, session_stats, entries, enrolled_students } = data;
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  const handleDelete = async (nameConfirm) => {
    try {
      await api.delete(`/public/planner/entries/${deletingEntry.id}?name_confirm=${encodeURIComponent(nameConfirm)}`);
      toast.success('Ligne supprimée');
      setDeletingEntry(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.detail || 'Nom incorrect');
    }
  };

  return (
    <div className="mt-4">
      {/* Header stats */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <colgroup>
              <col className="w-40" />
              <col className="w-16" />
              {sessions.map(s => <col key={s.id} className="w-20" />)}
            </colgroup>
            <thead>
              {/* Row 1: session dates */}
              <tr className="border-b border-slate-100">
                <th className="px-4 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  {course.name}
                  {course.location && <div className="text-[10px] font-normal text-slate-400">{course.location}</div>}
                </th>
                <th className="px-2 py-3 text-center text-[11px] font-bold text-slate-400">Rôle</th>
                {sessions.map(s => {
                  const past = s.datetime_start && isPast(parseISO(s.datetime_start));
                  return (
                    <th key={s.id} className={cn('px-2 py-3 text-center', past && 'opacity-50')}>
                      <SessionLabel session={s} />
                    </th>
                  );
                })}
              </tr>
              {/* Row 2: counters per session */}
              <tr className="bg-slate-50 border-b border-slate-100">
                <td className="px-4 py-2 text-[11px] text-slate-500 font-semibold">Total présents</td>
                <td />
                {sessions.map(s => {
                  const st = session_stats[s.id] || {};
                  return (
                    <td key={s.id} className="px-2 py-2 text-center">
                      <div className="text-xs font-bold text-slate-700">{st.total || 0}</div>
                      <div className="flex justify-center gap-1 mt-0.5">
                        <span className="text-[10px] text-blue-500 font-semibold">{st.leaders || 0}L</span>
                        <span className="text-[10px] text-pink-500 font-semibold">{st.followers || 0}F</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </thead>

            {/* Entry rows */}
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={2 + sessions.length} className="px-4 py-8 text-center text-slate-400 text-sm italic">
                    Aucune disponibilité enregistrée pour ce cours
                  </td>
                </tr>
              )}
              {entries.map(entry => (
                <tr key={entry.id} className="border-b border-slate-50 hover:bg-slate-50/50 group">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700 truncate max-w-[120px]">{entry.name}</span>
                      <div className="hidden group-hover:flex items-center gap-1 shrink-0">
                        <button onClick={() => { setEditingEntry(entry); setShowForm(false); }}
                          className="text-slate-400 hover:text-[#0288D1] p-0.5 transition-colors"
                          title="Modifier" data-testid={`edit-entry-${entry.id}`}>
                          <Pencil size={11} />
                        </button>
                        <button onClick={() => setDeletingEntry(entry)}
                          className="text-slate-400 hover:text-red-500 p-0.5 transition-colors"
                          title="Supprimer" data-testid={`delete-entry-${entry.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-center"><RoleBadge role={entry.role} /></td>
                  {sessions.map(s => {
                    const r = entry.session_responses?.[s.id] ?? 0;
                    const cfg = RESP_CONFIG[r];
                    const past = s.datetime_start && isPast(parseISO(s.datetime_start));
                    return (
                      <td key={s.id} className={cn('px-2 py-2.5 text-center', past && 'opacity-40')}>
                        <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center mx-auto touch-target cursor-pointer', cfg.bg)}>
                          {cfg.icon}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add button or form */}
      {!editingEntry && !showForm && (
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-sm font-semibold text-[#0288D1] hover:text-[#0277BE] bg-[#0288D1]/8 hover:bg-[#0288D1]/15 px-4 py-2.5 rounded-xl transition-all"
          data-testid="planner-add-btn">
          <Plus size={15} /> Ajouter mes disponibilités
        </button>
      )}

      {showForm && !editingEntry && (
        <EntryForm
          courseId={course.id}
          sessions={sessions}
          enrolledStudents={enrolled_students}
          onSaved={() => { setShowForm(false); onRefresh(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingEntry && (
        <EntryForm
          courseId={course.id}
          sessions={sessions}
          enrolledStudents={enrolled_students}
          existingEntry={editingEntry}
          onSaved={() => { setEditingEntry(null); onRefresh(); }}
          onCancel={() => setEditingEntry(null)}
        />
      )}

      {deletingEntry && (
        <DeleteConfirm
          entry={deletingEntry}
          onConfirm={handleDelete}
          onCancel={() => setDeletingEntry(null)}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PlannerPublic({ isAdmin = false, embedded = false }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);

  useEffect(() => {
    api.get('/public/planner/courses')
      .then(data => setCourses(data || []))
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  const fetchGrid = useCallback(async (courseId) => {
    setGridLoading(true);
    try {
      const data = await api.get(`/public/planner/${courseId}?admin=${isAdmin}`);
      setGridData(data);
    } catch {
      toast.error('Erreur de chargement du cours');
    } finally {
      setGridLoading(false);
    }
  }, [isAdmin]);

  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    fetchGrid(course.id);
  };

  if (!embedded) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0288D1] rounded-xl flex items-center justify-center">
              <Users size={15} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 text-sm leading-tight">Planificateur</h1>
              <p className="text-[11px] text-slate-400 leading-none">Indiquez vos disponibilités</p>
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-6">
          <PlannerContent
            courses={courses} loading={loading}
            selectedCourse={selectedCourse} gridData={gridData} gridLoading={gridLoading}
            onSelectCourse={handleSelectCourse} onRefresh={() => fetchGrid(selectedCourse?.id)}
            onBack={() => { setSelectedCourse(null); setGridData(null); }}
            isAdmin={isAdmin}
          />
        </main>
      </div>
    );
  }

  // Embedded (in admin tab)
  return (
    <>
      {!embedded && <MobileHeader title={selectedCourse?.name || 'Planificateur'} />}  {/* v2.28 : intégré au Profil → pas de 2e titre */}
      <PlannerContent
        courses={courses} loading={loading}
        selectedCourse={selectedCourse} gridData={gridData} gridLoading={gridLoading}
        onSelectCourse={handleSelectCourse} onRefresh={() => fetchGrid(selectedCourse?.id)}
        onBack={() => { setSelectedCourse(null); setGridData(null); }}
        isAdmin={isAdmin}
      />
    </>
  );
}

// ── Shared content ────────────────────────────────────────────────────────────
function PlannerContent({ courses, loading, selectedCourse, gridData, gridLoading, onSelectCourse, onRefresh, onBack, isAdmin }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-4 border-[#0288D1]/30 border-t-[#0288D1] rounded-full animate-spin" />
      </div>
    );
  }

  // Course selected → show grid
  if (selectedCourse) {
    return (
      <div>
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
          <ChevronLeft size={15} /> Tous les cours
        </button>

        {gridLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-[#0288D1]/30 border-t-[#0288D1] rounded-full animate-spin" />
          </div>
        ) : gridData ? (
          <DoodleGrid data={gridData} onRefresh={onRefresh} isAdmin={isAdmin} />
        ) : null}
      </div>
    );
  }

  // Course list
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-800 font-manrope">Choisissez votre cours</h2>
        <p className="text-sm text-slate-400 mt-1">Sélectionnez un cours pour indiquer vos disponibilités</p>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">Aucun cours actif disponible</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map(course => (
            <button key={course.id} onClick={() => onSelectCourse(course)}
              className="bg-white rounded-2xl border border-slate-100 p-5 text-left hover:border-[#0288D1]/40 hover:shadow-md transition-all group"
              data-testid={`planner-course-${course.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 bg-[#0288D1]/10 rounded-xl flex items-center justify-center group-hover:bg-[#0288D1]/20 transition-colors">
                  <Users size={16} className="text-[#0288D1]" />
                </div>
                <span className="text-xs font-semibold text-slate-400">{course.enrolled_count} inscrits</span>
              </div>
              <div className="font-bold text-slate-800 text-sm mt-3 leading-tight">{course.name}</div>
              {/* Caractéristiques du cours */}
              <div className="mt-2 space-y-1">
                {(course.day || course.time) && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0288D1]">
                    <Calendar size={11} className="shrink-0" />
                    {[course.day, course.time].filter(Boolean).join(' · ')}
                  </div>
                )}
                {course.location && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <MapPin size={11} className="shrink-0 text-slate-400" />
                    {course.location}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
